import React, { useRef, useState, useEffect } from 'react';
import { IconButton, Box, Typography, Select, MenuItem, Chip } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';

// Hook personalizado para detectar cambios en el tamaño de la ventana
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });
  
  useEffect(() => {
    // Verificar que estamos en el navegador
    if (typeof window === 'undefined') return;
    
    // Función para actualizar el estado con las nuevas dimensiones
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    
    // Agregar event listener
    window.addEventListener('resize', handleResize);
    
    // Limpiar event listener al desmontar
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return windowSize;
};

/**
 * Componente de botón unificado para DirectiveAI
 * @param {Object} props - Propiedades del componente
 * @param {string} [props.variant='default'] - Variante del botón: 'default', 'primary', 'menu', 'icon', 'audio', 'recording', 'modelSelector', 'personalitySelector', 'searchButton', 'visionButton', 'fileAttachment'
 * @param {boolean} [props.isActive=false] - Estado activo del botón
 * @param {boolean} [props.disabled=false] - Estado deshabilitado del botón
 * @param {boolean} [props.isWaitingForResponse=false] - Indica si se está esperando una respuesta
 * @param {boolean} [props.isRecording=false] - Indica si está en estado de grabación
 * @param {React.ReactNode} [props.icon] - Icono del botón
 * @param {React.ReactNode} [props.children] - Contenido del botón (para variantes con texto)
 * @param {Function} [props.onClick] - Función a ejecutar al hacer clic
 * @param {string} [props.title] - Título para el tooltip
 * @param {Object} [props.sx] - Estilos adicionales
 * @param {string} [props.color] - Color personalizado
 * @param {string} [props.size='medium'] - Tamaño del botón: 'small', 'medium', 'large'
 * @param {string} [props.selectedValue] - Valor seleccionado en el selector
 * @param {Function} [props.onValueChange] - Función para cambiar el valor seleccionado
 * @param {Array} [props.options] - Opciones para el selector
 * @param {boolean} [props.isPro] - Si el usuario es PRO
 * @param {boolean} [props.isMobile] - Si el dispositivo es móvil (opcional, se calculará automáticamente si no se proporciona)
 * @param {number} [props.mobileBreakpoint=768] - Punto de corte para definir vista móvil (en píxeles)
 */
const AGTButton = ({
  variant = 'default',
  isActive = false,
  disabled = false,
  isWaitingForResponse = false,
  isRecording = false,
  icon,
  children,
  onClick,
  title,
  sx = {},
  color,
  size = 'medium',
  // Propiedades específicas para modelSelector y personalitySelector
  selectedValue,
  onValueChange,
  options = [],
  isPro = false,
  isMobile: propIsMobile, // Renombramos para evitar confusión
  mobileBreakpoint = 768, // Punto de corte para definir vista móvil (en píxeles)
  ...rest
}) => {
  // Usamos el hook para obtener el tamaño actual de la ventana
  const { width } = useWindowSize();
  
  // Determinamos si estamos en móvil basándonos en el ancho de la ventana
  // Si se proporciona isMobile como prop, usamos ese valor, de lo contrario calculamos
  const isMobile = propIsMobile !== undefined ? propIsMobile : width < mobileBreakpoint;
  
  const isDisabled = disabled || isWaitingForResponse;

  // Renderizado condicional basado en la variante
  switch (variant) {
    case 'primary':
      return (
        <StyledBlueButton 
          onClick={isDisabled ? undefined : onClick}
          isRecording={isRecording}
          disabled={isDisabled}
          title={title}
          size={size}
          className={`agt-button primary-button ${isActive ? 'active' : ''}`}
          sx={sx}
          {...rest}
        >
          {icon && React.cloneElement(icon, { 
            size: size === 'small' ? 20 : (size === 'large' ? 28 : 24) 
          })}
        </StyledBlueButton>
      );

    case 'menu':
      return (
        <StyledMenuButton 
          onClick={isDisabled ? undefined : onClick}
          isActive={isActive}
          disabled={isDisabled}
          title={title}
          size={size}
          className={`agt-button menu-button ${isActive ? 'active' : ''}`}
          sx={sx}
          {...rest}
        >
          {icon && React.cloneElement(icon, { 
            size: size === 'small' ? 16 : (size === 'large' ? 22 : 18) 
          })}
        </StyledMenuButton>
      );

    case 'icon':
      return (
        <StyledIconContainer 
          onClick={isDisabled ? undefined : onClick}
          disabled={isDisabled}
          title={title}
          className={`agt-button icon-container ${isActive ? 'active' : ''}`}
          sx={{
            ...sx,
            color: isActive ? '#0071E3' : 'inherit',
            height: '36px', // Forzar altura a 36px
          }}
          {...rest}
        >
          <StyledIconButton 
            $active={isActive}
            $color={color}
            $size={size}
            className="icon-button"
            disabled={isDisabled}
          >
            {icon && React.cloneElement(icon, { 
              size: size === 'small' ? 14 : (size === 'large' ? 20 : 16) 
            })}
          </StyledIconButton>
          {children && (
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 500, 
                fontSize: size === 'small' ? '0.75rem' : '0.875rem',
                opacity: isDisabled ? 0.5 : 1,
                ml: 1,
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
              }}
            >
              {children}
            </Typography>
          )}
        </StyledIconContainer>
      );

    case 'audio':
      return (
        <StyledAudioButton
          onClick={isDisabled ? undefined : onClick}
          disabled={isDisabled}
          title={title}
          size={size}
          isRecording={isRecording}
          className={`agt-button audio-button ${isRecording ? 'recording' : ''}`}
          sx={sx}
          {...rest}
        >
          {icon && React.cloneElement(icon, { 
            size: size === 'small' ? 20 : (size === 'large' ? 28 : 24) 
          })}
        </StyledAudioButton>
      );

    case 'recording':
      return (
        <StyledRecordingButton
          onClick={isDisabled ? undefined : onClick}
          disabled={isDisabled}
          title={title}
          size={size}
          className="agt-button recording-button"
          sx={sx}
          {...rest}
        >
          {icon && React.cloneElement(icon, { 
            size: size === 'small' ? 16 : (size === 'large' ? 20 : 18) 
          })}
        </StyledRecordingButton>
      );

    case 'modelSelector':
      return (
        <ModelSelectorComponent
          selectedValue={selectedValue}
          onValueChange={onValueChange}
          options={options}
          disabled={isDisabled}
          isPro={isPro}
          isMobile={isMobile}
          icon={icon}
          size={size}
          sx={sx}
          {...rest}
        />
      );

    case 'personalitySelector':
      return (
        <PersonalitySelectorComponent
          selectedValue={selectedValue}
          onValueChange={onValueChange}
          options={options}
          disabled={isDisabled}
          isPro={isPro}
          isMobile={isMobile}
          icon={icon}
          size={size}
          sx={sx}
          {...rest}
        />
      );
      
    case 'searchButton':
      return (
        <FeatureButtonComponent
          onClick={isDisabled ? undefined : onClick}
          disabled={isDisabled}
          isPro={isPro}
          isMobile={isMobile}
          icon={icon}
          isActive={isActive}
          title={title}
          featureType="search"
          size={size}
          sx={sx}
          {...rest}
        >
          {children || "AGT Search"}
        </FeatureButtonComponent>
      );
      
    case 'visionButton':
      return (
        <FeatureButtonComponent
          onClick={isDisabled ? undefined : onClick}
          disabled={isDisabled}
          isPro={isPro}
          isMobile={isMobile}
          icon={icon}
          isActive={isActive}
          title={title}
          featureType="vision"
          size={size}
          sx={sx}
          {...rest}
        >
          {children || "AGT Vision"}
        </FeatureButtonComponent>
      );
      
    case 'fileAttachment':
      return (
        <StyledFileAttachmentButton
          onClick={isDisabled ? undefined : onClick}
          disabled={isDisabled}
          title={title}
          size={size}
          isActive={isActive}
          className={`agt-button file-attachment-button ${isActive ? 'active' : ''}`}
          sx={sx}
          {...rest}
        >
          {icon && React.cloneElement(icon, { 
            size: size === 'small' ? 16 : (size === 'large' ? 22 : 18) 
          })}
        </StyledFileAttachmentButton>
      );

    default:
      return (
        <StyledDefaultButton
          onClick={isDisabled ? undefined : onClick}
          disabled={isDisabled}
          title={title}
          size={size}
          className={`agt-button default-button ${isActive ? 'active' : ''}`}
          sx={sx}
          {...rest}
        >
          {icon && React.cloneElement(icon, { 
            size: size === 'small' ? 16 : (size === 'large' ? 22 : 18) 
          })}
          {children && (
            <Box sx={{ ml: icon ? 1 : 0 }}>
              {children}
            </Box>
          )}
        </StyledDefaultButton>
      );
  }
};

// Componente para botones de características (Search y Vision)
const FeatureButtonComponent = ({
  onClick,
  disabled = false,
  isPro = false,
  isMobile = false,
  icon,
  isActive = false,
  title,
  children,
  featureType, // "search" o "vision"
  size = 'medium',
  sx = {}
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const { t } = useTranslation();
 
  // Determinar si la función requiere PRO
  const requiresPro = featureType === 'vision'; // Asumiendo que Vision requiere PRO
 
  // Forzar altura a 36px independientemente del tamaño
  const buttonHeight = '36px';
  const paddingX = isMobile ? '12px' : '14px'; // Aumentado el padding horizontal
  const paddingY = '6px';
  const fontSize = '0.875rem';
  const iconSize = 18;
 
  // Clonar el icono con el tamaño adecuado si existe
  const sizedIcon = icon ? React.cloneElement(icon, { size: iconSize }) : null;
 
  // Si es móvil, mostrar solo el icono
  if (isMobile) {
    return (
      <Box
        onClick={!disabled && onClick}
        title={title}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          cursor: disabled ? 'not-allowed' : 'pointer',
          borderRadius: '50%', // Circular for icon only
          backgroundColor: isWallpaperMode
            ? 'rgba(128, 128, 128, 0.3)'
            : isActive
              ? isDarkMode
                ? 'rgba(255, 255, 255, 0.12)'
                : 'rgba(255, 255, 255, 0.95)'
              : isDarkMode
                ? 'rgba(255, 255, 255, 0.08)'
                : 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          border: `1px solid ${
            isWallpaperMode
              ? isActive
                ? '#FFFFFF'
                : 'rgba(128, 128, 128, 0.3)'
              : isActive
                ? '#0071E3'
                : isDarkMode
                  ? 'rgba(255, 255, 255, 0.15)'
                  : 'rgba(0, 0, 0, 0.1)'
          }`,
          boxShadow: isDarkMode || isWallpaperMode ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.05)',
          padding: '8px',
          width: buttonHeight,
          height: buttonHeight,
          transition: 'all 0.2s ease',
          opacity: disabled ? 0.5 : 1,
          '&:hover': {
            backgroundColor: !disabled && (
              isWallpaperMode
                ? 'rgba(128, 128, 128, 0.4)'
                : isDarkMode
                  ? 'rgba(255, 255, 255, 0.12)'
                  : 'rgba(255, 255, 255, 0.95)'
            )
          },
          '&:active': {
            transform: !disabled && 'scale(0.98)',
          },
          ...sx
        }}
      >
        {/* Button icon */}
        <Box
          sx={{
            color: isWallpaperMode
              ? '#FFFFFF'
              : isActive
                ? '#0071E3'
                : disabled
                  ? isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)'
                  : '#0071E3',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: disabled ? 0.7 : 1,
          }}
        >
          {sizedIcon}
        </Box>
      </Box>
    );
  }
 
  return (
    <Box
      onClick={!disabled && onClick}
      title={title}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        position: 'relative',
        cursor: disabled ? 'not-allowed' : 'pointer',
        borderRadius: '28px',
        backgroundColor: isWallpaperMode
          ? 'rgba(55, 55, 55, 0.1)'
          : isActive
            ? isDarkMode
              ? 'rgba(255, 255, 255, 0.12)'
              : 'rgba(255, 255, 255, 0.95)'
            : isDarkMode
              ? 'rgba(255, 255, 255, 0.08)'
              : 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        border: `1px solid ${
          isWallpaperMode
            ? isActive
              ? '#FFFFFF'
              : 'rgba(128, 128, 128, 0.3)'
            : isActive
              ? '#0071E3'
              : isDarkMode
                ? 'rgba(255, 255, 255, 0.15)'
                : 'rgba(0, 0, 0, 0.1)'
        }`,
        boxShadow: isDarkMode || isWallpaperMode ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.05)',
        padding: `${paddingY} ${paddingX}`,
        height: buttonHeight,
        transition: 'all 0.2s ease',
        opacity: disabled ? 0.5 : 1,
        '&:hover': {
          backgroundColor: !disabled && (
            isDarkMode
              ? 'rgba(255, 255, 255, 0.12)'
              : 'rgba(255, 255, 255, 0.95)'
          )
        },
        '&:active': {
          transform: !disabled && 'scale(0.98)',
        },
        ...sx
      }}
    >
      {/* Icono del botón */}
      <Box
        sx={{
          color: isActive 
            ? '#0071E3' 
            : disabled 
              ? isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)' 
              : '#0071E3',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mr: 1.5, // Aumentado para que coincida con los selectores
          opacity: disabled ? 0.7 : 1,
        }}
      >
        {sizedIcon}
      </Box>
 
      {/* Texto del botón */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'flex-start'
      }}>
        <Typography
          sx={{
            fontSize: fontSize,
            fontWeight: 500,
            color: disabled 
              ? isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)'
              : isActive ? '#0071E3' : 'inherit',
            whiteSpace: 'nowrap',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
            lineHeight: 1.2,
          }}
        >
          {children}
        </Typography>
 
        {/* Texto "Upgrade to Pro" si es necesario pero sin etiqueta PRO */}
        {requiresPro && !isPro && (
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: disabled 
                ? isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'
                : 'text.secondary',
              whiteSpace: 'nowrap',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
              lineHeight: 1.1,
            }}
          >
            {t('features.upgradeToPro')}
          </Typography>
        )}
      </Box>
 
      {/* Se elimina el chip PRO */}
    </Box>
  );
};

// Componente de ModelSelector
const ModelSelectorComponent = ({
  selectedValue,
  onValueChange,
  options = [],
  disabled = false,
  isPro = false,
  isMobile = false,
  icon,
  size = 'medium',
  sx = {}
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const { t } = useTranslation();
  const selectRef = useRef(null);

  // Forzar altura a 36px independientemente del tamaño
  const selectHeight = '36px';
  const fontSize = '0.875rem';
  const iconSize = 18;
  const paddingY = '6px';
  
  // Clonar el icono con el tamaño adecuado si existe
  const sizedIcon = icon ? React.cloneElement(icon, { size: iconSize }) : null;

  // Función para convertir el nombre completo en el abreviado
  const getAbbreviatedName = (fullName) => {
    if (fullName === 'AGT Nest') return 'AGT';
    if (fullName === 'AGT Pro') return 'Pro';
    if (fullName === 'AGT Ultra') return 'Ultra';
    return fullName;
  };

  // Función para manejar clicks en el contenedor y abrir el selector
  const handleContainerClick = (e) => {
    if (disabled) return;
    
    // Si el usuario hace clic en cualquier parte del contenedor, abrimos el select
    if (selectRef.current && isMobile) {
      // Simular un clic en el select
      const selectElement = selectRef.current.querySelector('.MuiSelect-select');
      if (selectElement) {
        selectElement.click();
      }
    }
  };

  const CustomChevronIcon = () => {
    return (
      <ChevronDown
        size={iconSize}
        style={{
          color: disabled
            ? isWallpaperMode
              ? 'rgba(255, 255, 255, 0.3)'
              : isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)'
            : isWallpaperMode
              ? '#FFFFFF'
              : isDarkMode ? '#fff' : '#000',
          opacity: disabled ? 0.6 : 0.7,
          marginRight: '4px',
          position: 'absolute',
          right: isMobile ? '4px' : '8px',
          // Ocultar chevron en modo móvil
          display: isMobile ? 'none' : 'block'
        }}
      />
    );
  };

  return (
    <Box
      onClick={handleContainerClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        position: 'relative',
        height: selectHeight,
        // Estilos similares a los botones de características
        borderRadius: isMobile ? '50%' : '28px',
        backgroundColor: isWallpaperMode
          ? 'rgba(128, 128, 128, 0.3)'
          : isDarkMode
            ? 'rgba(255, 255, 255, 0.08)'
            : 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        border: isWallpaperMode
          ? `1px solid rgba(128, 128, 128, 0.3)`
          : `1px solid ${
            isDarkMode
              ? 'rgba(255, 255, 255, 0.15)'
              : 'rgba(0, 0, 0, 0.1)'
          }`,
        boxShadow: isWallpaperMode ? 'none' : (isDarkMode ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.05)'),
        transition: 'all 0.2s ease',
        opacity: disabled ? 0.5 : 1, // Homologado a 0.5 para estado deshabilitado
        cursor: disabled ? 'not-allowed' : 'pointer', // Cursor consistente
        width: isMobile ? selectHeight : 'auto',
        minWidth: isMobile ? selectHeight : '120px',
        maxWidth: isMobile ? selectHeight : '200px',
        '&:hover': {
          backgroundColor: !disabled && (
            isWallpaperMode
              ? 'rgba(128, 128, 128, 0.4)'
              : isDarkMode
                ? 'rgba(255, 255, 255, 0.12)'
                : 'rgba(255, 255, 255, 0.95)'
          )
        },
        ...sx
      }}
      ref={selectRef}
    >
      {/* Icono del selector */}
      <Box
        sx={{
          position: 'absolute',
          left: isMobile ? '50%' : '14px',
          transform: isMobile ? 'translateX(-50%)' : 'none',
          zIndex: 1,
          color: disabled
            ? isWallpaperMode
              ? 'rgba(255, 255, 255, 0.3)'
              : isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)'
            : isWallpaperMode
              ? '#FFFFFF'
              : '#0071E3',
          opacity: disabled ? 0.7 : 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none', // Importante: no interceptar eventos de clic
        }}
      >
        {sizedIcon}
      </Box>

      <Select
        value={selectedValue}
        onChange={(e) => onValueChange(e.target.value)}
        disabled={disabled}
        IconComponent={CustomChevronIcon}
        sx={{
          width: '100%',
          height: selectHeight,
          cursor: 'pointer',
          '& .MuiSelect-select': {
            paddingLeft: isMobile ? '0 !important' : '44px !important',
            paddingRight: isMobile ? '0 !important' : '28px !important',
            paddingTop: `${paddingY} !important`,
            paddingBottom: `${paddingY} !important`,
            backgroundColor: 'transparent', // Transparente para que se vea el fondo del Box
            border: 'none', // Sin borde, ya que el Box tiene borde
            boxShadow: 'none', // Sin sombra, ya que el Box tiene sombra
            transition: 'all 0.2s ease',
            '&:focus': {
              backgroundColor: 'transparent',
            },
            minHeight: 'auto',
            height: selectHeight,
            lineHeight: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: isMobile ? 'center' : 'flex-start',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
            fontSize: fontSize,
          },
          '&.Mui-disabled': {
            '& .MuiSelect-select': {
              color: isDarkMode 
                ? 'rgba(255, 255, 255, 0.3)' 
                : 'rgba(0, 0, 0, 0.3)',
              cursor: 'not-allowed',
            },
            cursor: 'not-allowed',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none'
          },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              mt: 1,
              borderRadius: '22px',
              backgroundColor: isWallpaperMode
                ? 'rgba(128, 128, 128, 0.3)'
                : isDarkMode ? '#2c2c2c' : '#ffffff',
              backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
              WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
              boxShadow: isWallpaperMode
                ? 'none'
                : isDarkMode
                  ? '0 4px 20px rgba(0, 0, 0, 0.5)'
                  : '0 4px 20px rgba(0, 0, 0, 0.15)',
              border: isWallpaperMode
                ? `1px solid rgba(128, 128, 128, 0.3)`
                : `1px solid ${
                isDarkMode
                  ? 'rgba(255, 255, 255, 0.15)'
                  : 'rgba(0, 0, 0, 0.1)'
              }`,
              '& .MuiMenuItem-root': {
                padding: '10px 14px',
                borderRadius: '16px',
                margin: '2px 4px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                fontSize: fontSize,
                color: isWallpaperMode ? 'rgba(255, 255, 255, 0.7)' : 'inherit',
                '&:hover': {
                  backgroundColor: isWallpaperMode
                    ? 'rgba(128, 128, 128, 0.4)'
                    : isDarkMode
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'rgba(0, 0, 0, 0.05)'
                },
                '&.Mui-disabled': {
                  opacity: 0.7,
                  color: isWallpaperMode
                    ? 'rgba(255, 255, 255, 0.3)'
                    : isDarkMode
                      ? 'rgba(255, 255, 255, 0.3)'
                      : 'rgba(0, 0, 0, 0.3)'
                }
              },
              minWidth: '180px'
            }
          },
          transformOrigin: {
            horizontal: 'left',
            vertical: 'top'
          },
          anchorOrigin: {
            horizontal: 'left',
            vertical: 'bottom'
          }
        }}
        renderValue={(selected) => {
          if (!selected) {
            return (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                height: '100%'
              }}>
                {!isMobile && (
                  <Typography
                    sx={{
                      fontSize: fontSize,
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      opacity: disabled ? 0.7 : 1,
                      color: disabled
                        ? isWallpaperMode
                          ? 'rgba(255, 255, 255, 0.3)'
                          : isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'
                        : isWallpaperMode
                          ? 'rgba(255, 255, 255, 0.7)'
                          : 'inherit',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                    }}
                  >
                    {t('model.chooseModel')}
                  </Typography>
                )}
              </Box>
            );
          }

          // Buscamos el objeto del modelo seleccionado
          const selectedModelObj = options.find((m) => m.id === selected);
          if (!selectedModelObj) return null;

          // Mostrar el nombre abreviado
          const displayName = getAbbreviatedName(selectedModelObj.name);

          // En modo móvil, no mostrar ningún texto
          if (isMobile) {
            return <Box sx={{ width: '100%', height: '100%' }} />;
          }

          // Crear diseño similar a los botones de características
          return (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              height: '100%'
            }}>
              <Typography
                sx={{
                  fontSize: fontSize,
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  opacity: disabled ? 0.7 : 1,
                  color: disabled 
                    ? isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'
                    : 'inherit',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                }}
              >
                {displayName}
              </Typography>
              
              {selectedModelObj.proOnly && !isPro && (
                <Chip
                  label="PRO"
                  size="small"
                  sx={{
                    height: '18px',
                    backgroundColor: '#0071E3',
                    color: 'white',
                    fontSize: '0.625rem',
                    fontWeight: 600,
                    borderRadius: '28px',
                    marginLeft: '8px',
                    marginRight: '4px',
                    opacity: disabled ? 0.5 : 1,
                    '& .MuiChip-label': {
                      px: 1,
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                    }
                  }}
                />
              )}
            </Box>
          );
        }}
      >
        {/* Menú de opciones - mantener igual */}
        {options.map((model) => (
          <MenuItem
            key={model.id}
            value={model.id}
            disabled={model.disabledForAll || (model.proOnly && !isPro)}
            sx={{
              opacity: model.disabledForAll || (model.proOnly && !isPro) ? 0.7 : 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              '&.Mui-disabled': {
                opacity: 0.7,
                color: isDarkMode 
                  ? 'rgba(255, 255, 255, 0.3)' 
                  : 'rgba(0, 0, 0, 0.3)'
              }
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%'
              }}
            >
              <Typography 
                sx={{ 
                  fontSize: fontSize, 
                  fontWeight: 500,
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                }}
              >
                {model.name}
              </Typography>
              {model.proOnly && (
                <Chip
                  label="PRO"
                  size="small"
                  sx={{
                    height: '18px',
                    backgroundColor: '#0071E3',
                    color: 'white',
                    fontSize: '0.625rem',
                    fontWeight: 600,
                    borderRadius: '28px',
                    '& .MuiChip-label': {
                      px: 1,
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                    }
                  }}
                />
              )}
            </Box>
            <Typography
              sx={{ 
                fontSize: '0.75rem', 
                color: 'text.secondary', 
                mt: 0.5,
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
              }}
            >
              {model.description}
            </Typography>
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};

// Componente de PersonalitySelector
const PersonalitySelectorComponent = ({
  selectedValue,
  onValueChange,
  options = [],
  disabled = false,
  isPro = false,
  isMobile = false,
  icon,
  size = 'medium',
  sx = {}
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const { t } = useTranslation();
  const selectRef = useRef(null);

  // Forzar altura a 36px independientemente del tamaño
  const selectHeight = '36px';
  const fontSize = '0.875rem';
  const iconSize = 18;
  const paddingY = '6px';
  
  // Clonar el icono con el tamaño adecuado si existe
  const sizedIcon = icon ? React.cloneElement(icon, { size: iconSize }) : null;

  // Función para convertir el nombre completo en el abreviado (similar a ModelSelector)
  const getAbbreviatedName = (fullName) => {
    // Para personalidades no necesitamos abreviar, pero mantenemos
    // la función para consistencia con ModelSelector
    return fullName;
  };

  // Función para manejar clicks en el contenedor y abrir el selector
  const handleContainerClick = (e) => {
    if (disabled) return;
    
    // Si el usuario hace clic en cualquier parte del contenedor, abrimos el select
    if (selectRef.current && isMobile) {
      // Simular un clic en el select
      const selectElement = selectRef.current.querySelector('.MuiSelect-select');
      if (selectElement) {
        selectElement.click();
      }
    }
  };

  const CustomChevronIcon = () => {
    return (
      <ChevronDown
        size={iconSize}
        style={{
          color: disabled
            ? isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)'
            : isDarkMode ? '#fff' : '#000',
          opacity: disabled ? 0.6 : 0.7,
          marginRight: '4px',
          position: 'absolute',
          right: isMobile ? '4px' : '12px', // Cambiado de 8px a 12px para mover más a la derecha
          // Ocultar chevron en modo móvil
          display: isMobile ? 'none' : 'block'
        }}
      />
    );
  };

  return (
    <Box
      onClick={handleContainerClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        position: 'relative',
        height: selectHeight,
        // Estilos similares a los botones de características
        borderRadius: isMobile ? '50%' : '28px',
        backgroundColor: isWallpaperMode
          ? 'rgba(128, 128, 128, 0.3)'
          : isDarkMode
            ? 'rgba(255, 255, 255, 0.08)'
            : 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        border: isWallpaperMode
          ? `1px solid rgba(128, 128, 128, 0.3)`
          : `1px solid ${
            isDarkMode
              ? 'rgba(255, 255, 255, 0.15)'
              : 'rgba(0, 0, 0, 0.1)'
          }`,
        boxShadow: isWallpaperMode ? 'none' : (isDarkMode ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.05)'),
        transition: 'all 0.2s ease',
        opacity: disabled ? 0.5 : 1, // Homologado a 0.5 para estado deshabilitado
        cursor: disabled ? 'not-allowed' : 'pointer', // Cursor consistente
        width: isMobile ? selectHeight : 'auto',
        minWidth: isMobile ? selectHeight : '120px',
        maxWidth: isMobile ? selectHeight : '200px',
        '&:hover': {
          backgroundColor: !disabled && (
            isWallpaperMode
              ? 'rgba(128, 128, 128, 0.4)'
              : isDarkMode
                ? 'rgba(255, 255, 255, 0.12)'
                : 'rgba(255, 255, 255, 0.95)'
          )
        },
        ...sx
      }}
      ref={selectRef}
    >
      {/* Icono del selector */}
      <Box
        sx={{
          position: 'absolute',
          left: isMobile ? '50%' : '14px', // Ajustado para que sea igual al ModelSelector
          transform: isMobile ? 'translateX(-50%)' : 'none',
          zIndex: 1,
          color: disabled 
            ? isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)' 
            : '#0071E3',
          opacity: disabled ? 0.7 : 1, // Consistencia en la opacidad
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none', // Importante: no interceptar eventos de clic
        }}
      >
        {sizedIcon}
      </Box>

      <Select
        value={selectedValue}
        onChange={(e) => onValueChange(e.target.value)}
        disabled={disabled}
        IconComponent={CustomChevronIcon}
        sx={{
          width: '100%',
          height: selectHeight,
          cursor: 'pointer',
          '& .MuiSelect-select': {
            paddingLeft: isMobile ? '0 !important' : '44px !important',
            // Aumentar el paddingRight para dar más espacio entre el texto y el chevron
            paddingRight: isMobile ? '0 !important' : '36px !important', // Aumentado de 28px a 36px
            paddingTop: `${paddingY} !important`,
            paddingBottom: `${paddingY} !important`,
            backgroundColor: 'transparent',
            border: 'none',
            boxShadow: 'none',
            transition: 'all 0.2s ease',
            '&:focus': {
              backgroundColor: 'transparent',
            },
            minHeight: 'auto',
            height: selectHeight,
            lineHeight: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: isMobile ? 'center' : 'flex-start',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
            fontSize: fontSize,
          },
          '&.Mui-disabled': {
            '& .MuiSelect-select': {
              color: isDarkMode 
                ? 'rgba(255, 255, 255, 0.3)' 
                : 'rgba(0, 0, 0, 0.3)',
              cursor: 'not-allowed',
            },
            cursor: 'not-allowed',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none'
          },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              mt: 1,
              borderRadius: '22px',
              backgroundColor: isWallpaperMode
                ? 'rgba(128, 128, 128, 0.3)'
                : isDarkMode ? '#2c2c2c' : '#ffffff',
              backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
              WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
              boxShadow: isWallpaperMode
                ? 'none'
                : isDarkMode
                  ? '0 4px 20px rgba(0, 0, 0, 0.5)'
                  : '0 4px 20px rgba(0, 0, 0, 0.15)',
              border: isWallpaperMode
                ? `1px solid rgba(128, 128, 128, 0.3)`
                : `1px solid ${
                isDarkMode
                  ? 'rgba(255, 255, 255, 0.15)'
                  : 'rgba(0, 0, 0, 0.1)'
              }`,
              '& .MuiMenuItem-root': {
                padding: '10px 14px',
                borderRadius: '16px',
                margin: '2px 4px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                fontSize: fontSize,
                color: isWallpaperMode ? 'rgba(255, 255, 255, 0.7)' : 'inherit',
                '&:hover': {
                  backgroundColor: isWallpaperMode
                    ? 'rgba(128, 128, 128, 0.4)'
                    : isDarkMode
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'rgba(0, 0, 0, 0.05)'
                },
                '&.Mui-disabled': {
                  opacity: 0.7,
                  color: isWallpaperMode
                    ? 'rgba(255, 255, 255, 0.3)'
                    : isDarkMode
                      ? 'rgba(255, 255, 255, 0.3)'
                      : 'rgba(0, 0, 0, 0.3)'
                }
              },
              minWidth: '180px'
            }
          },
          transformOrigin: {
            horizontal: 'left',
            vertical: 'top'
          },
          anchorOrigin: {
            horizontal: 'left',
            vertical: 'bottom'
          }
        }}
        renderValue={(selected) => {
          if (!selected) {
            return (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                height: '100%'
              }}>
                {!isMobile && (
                  <Typography
                    sx={{
                      fontSize: fontSize,
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      opacity: disabled ? 0.7 : 1,
                      color: disabled
                        ? isWallpaperMode
                          ? 'rgba(255, 255, 255, 0.3)'
                          : isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'
                        : isWallpaperMode
                          ? 'rgba(255, 255, 255, 0.7)'
                          : 'inherit',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                    }}
                  >
                    {t('personality.choosePersonality')}
                  </Typography>
                )}
              </Box>
            );
          }

          // Buscamos el objeto de personalidad seleccionada
          const selectedPersonalityObj = options.find((p) => p.id === selected);
          if (!selectedPersonalityObj) return null;

          // En modo móvil, no mostrar ningún texto
          if (isMobile) {
            return <Box sx={{ width: '100%', height: '100%' }} />;
          }

          // Mostrar el nombre (sin abreviar para personalidades)
          const displayName = getAbbreviatedName(selectedPersonalityObj.name);

          // Crear diseño similar a los botones de características
          return (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              height: '100%'
            }}>
              <Typography
                sx={{
                  fontSize: fontSize,
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  opacity: disabled ? 0.7 : 1,
                  color: disabled 
                    ? isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'
                    : 'inherit',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                }}
              >
                {displayName}
              </Typography>
              
              {!isPro && selectedPersonalityObj.proOnly && (
                <Chip
                  label="PRO"
                  size="small"
                  sx={{
                    height: '18px',
                    backgroundColor: '#0071E3',
                    color: 'white',
                    fontSize: '0.625rem',
                    fontWeight: 600,
                    borderRadius: '28px',
                    marginLeft: '8px',
                    marginRight: '4px',
                    opacity: disabled ? 0.5 : 1,
                    '& .MuiChip-label': {
                      px: 1,
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                    }
                  }}
                />
              )}
            </Box>
          );
        }}
      >
        {/* Menú de opciones */}
        {options.map((personality) => (
          <MenuItem
            key={personality.id}
            value={personality.id}
            disabled={personality.proOnly && !isPro}
            sx={{
              opacity: (personality.proOnly && !isPro) ? 0.7 : 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%'
              }}
            >
              <Typography 
                sx={{ 
                  fontSize: fontSize, 
                  fontWeight: 500,
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                }}
              >
                {personality.name}
              </Typography>
              {personality.proOnly && (
                <Chip
                  label="PRO"
                  size="small"
                  sx={{
                    height: '18px',
                    backgroundColor: '#0071E3',
                    color: 'white',
                    fontSize: '0.625rem',
                    fontWeight: 600,
                    borderRadius: '28px',
                    '& .MuiChip-label': {
                      px: 1,
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                    }
                  }}
                />
              )}
            </Box>
            <Typography
              sx={{ 
                fontSize: '0.75rem', 
                color: 'text.secondary', 
                mt: 0.5,
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
              }}
            >
              {personality.description}
            </Typography>
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};

// Componentes estilizados
const StyledBlueButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'isRecording'
})(({ theme, isRecording, size }) => {
  // Mapeo de tamaños - forzar altura a 36px
  const sizeMap = {
    small: { width: '36px', height: '36px', minWidth: '36px', minHeight: '36px', padding: '2px' },
    medium: { width: '36px', height: '36px', minWidth: '36px', minHeight: '36px', padding: '3px' },
    large: { width: '36px', height: '36px', minWidth: '36px', minHeight: '36px', padding: '6px' }
  };

  const sizeProps = sizeMap[size] || sizeMap.medium;
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  // Colores consistentes con el sistema
  const appleBlue = '#0385FF';
  const appleRed = '#FF3B30';
  const wallpaperColor = '#2C2C2E';

  return {
    ...sizeProps,
    backgroundColor: isWallpaperMode
      ? (isRecording ? 'rgba(255, 59, 48, 0.7)' : wallpaperColor)
      : (isRecording ? appleRed : appleBlue),
    color: '#ffffff',
    borderRadius: '50%',
    transition: 'all 0.2s ease',
    margin: '0',
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode
      ? '1px solid rgba(255, 255, 255, 0.2)'
      : 'none',
    boxShadow: isWallpaperMode
      ? 'none'
      : theme.palette.mode === 'dark' ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.1)',
    '&:hover': {
      backgroundColor: isWallpaperMode
        ? (isRecording ? 'rgba(255, 59, 48, 0.8)' : '#3A3A3C')
        : (isRecording ? '#E02D26' : '#0275E6'),
    },
    '&:active': {
      transform: 'scale(0.95)',
      backgroundColor: isWallpaperMode
        ? (isRecording ? 'rgba(255, 59, 48, 0.9)' : '#48484A')
        : (isRecording ? '#D12420' : '#0264CC'),
    },
    '&:disabled': {
      backgroundColor: isWallpaperMode
        ? 'rgba(44, 44, 46, 0.5)'
        : theme.palette.mode === 'light' ? 'rgba(3, 133, 255, 0.5)' : 'rgba(3, 133, 255, 0.3)',
      color: '#ffffff',
      opacity: 0.5,
      cursor: 'not-allowed'
    }
  };
});

const StyledMenuButton = styled(IconButton)(({ theme, isActive, size }) => {
  // Mapeo de tamaños - forzar altura a 36px
  const sizeMap = {
    small: { padding: '4px', height: '36px' },
    medium: { padding: '6px', height: '36px' },
    large: { padding: '8px', height: '36px' }
  };
  
  const sizeProps = sizeMap[size] || sizeMap.medium;
  const isDarkMode = theme.palette.mode === 'dark';

  return {
    ...sizeProps,
    backgroundColor: isActive 
      ? isDarkMode 
        ? 'rgba(255, 255, 255, 0.15)' 
        : 'rgba(0, 0, 0, 0.07)'
      : isDarkMode 
        ? 'rgba(255, 255, 255, 0.08)' 
        : 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(10px)',
    boxShadow: isDarkMode ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.05)',
    border: `1px solid ${isActive 
      ? '#0071E3' 
      : isDarkMode 
        ? 'rgba(255, 255, 255, 0.15)' 
        : 'rgba(0, 0, 0, 0.1)'}`,
    borderRadius: '28px', // Más cuadrado al estilo Big Sur
    color: isActive ? '#0071E3' : 'inherit',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: isDarkMode 
        ? 'rgba(255, 255, 255, 0.12)' 
        : 'rgba(255, 255, 255, 0.95)'
    },
    '&:active': {
      backgroundColor: isDarkMode 
        ? 'rgba(255, 255, 255, 0.05)' 
        : 'rgba(0, 0, 0, 0.05)',
      transform: 'scale(0.98)',
    },
    '&:disabled': {
      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.5)',
      border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
      color: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
      opacity: 0.5, // Homologado a 0.5 para estado deshabilitado
      cursor: 'not-allowed',
    }
  };
});

const StyledIconContainer = styled(Box)(({ theme, disabled }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  
  return {
    display: 'inline-flex',
    alignItems: 'center',
    position: 'relative',
    cursor: disabled ? 'not-allowed' : 'pointer',
    width: 'auto',
    padding: '6px 12px',
    borderRadius: '28px',
    transition: 'background-color 0.2s ease',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    height: '36px', // Forzar altura a 36px
    opacity: disabled ? 0.5 : 1, // Homologado a 0.5 para estado deshabilitado
    '&:hover': {
      backgroundColor: disabled ? 'transparent' : isDarkMode 
        ? 'rgba(255, 255, 255, 0.1)' 
        : 'rgba(0, 0, 0, 0.05)'
    },
    '&:active': {
      transform: disabled ? 'none' : 'scale(0.98)',
    }
  };
});

const StyledIconButton = styled(Box)(({ theme, $active, $color, $size }) => {
  // Mapeo de tamaños
  const sizeMap = {
    small: { width: '16px', height: '16px' },
    medium: { width: '20px', height: '20px' },
    large: { width: '24px', height: '24px' }
  };
  
  const sizeProps = sizeMap[$size] || sizeMap.medium;

  return {
    ...sizeProps,
    color: $color || ($active ? '#0071E3' : theme.palette.text.primary),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&.Mui-disabled': {
      color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
      opacity: 0.7, // Consistencia en la opacidad
    }
  };
});

const StyledAudioButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'isRecording'
})(({ theme, isRecording, size }) => {
  // Mapeo de tamaños - forzar altura a 36px
  const sizeMap = {
    small: { width: '36px', height: '36px', minWidth: '36px', minHeight: '36px' },
    medium: { width: '36px', height: '36px', minWidth: '36px', minHeight: '36px' },
    large: { width: '36px', height: '36px', minWidth: '36px', minHeight: '36px' }
  };
  
  const sizeProps = sizeMap[size] || sizeMap.medium;
  
  // Apple red color
  const appleRed = "#FF3B30";

  return {
    ...sizeProps,
    padding: '3px',
    backgroundColor: 'transparent',
    color: isRecording ? appleRed : appleRed,
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&:active': {
      transform: 'scale(0.95)',
    },
    ...(isRecording ? {
      '&::after': {
        content: '""',
        position: 'absolute',
        top: '-3px',
        left: '-3px',
        right: '-3px',
        bottom: '-3px',
        borderRadius: '50%',
        border: `2px solid ${appleRed}`,
        animation: 'pulse 1.5s infinite',
      },
      '@keyframes pulse': {
        '0%': { boxShadow: `0 0 0 0 rgba(255, 59, 48, 0.7)` },
        '70%': { boxShadow: `0 0 0 8px rgba(255, 59, 48, 0)` },
        '100%': { boxShadow: `0 0 0 0 rgba(255, 59, 48, 0)` }
      }
    } : {}),
    '&:disabled': {
      color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
      backgroundColor: 'transparent',
      opacity: 0.5, // Homologado a 0.5 para estado deshabilitado
      cursor: 'not-allowed',
    }
  };
});

const StyledRecordingButton = styled(IconButton)(({ theme, size }) => {
  // Mapeo de tamaños - forzar altura a 36px
  const sizeMap = {
    small: { width: '36px', height: '36px', minWidth: '36px', minHeight: '36px' },
    medium: { width: '36px', height: '36px', minWidth: '36px', minHeight: '36px' },
    large: { width: '36px', height: '36px', minWidth: '36px', minHeight: '36px' }
  };
  
  const sizeProps = sizeMap[size] || sizeMap.medium;
  const isDarkMode = theme.palette.mode === 'dark';

  return {
    ...sizeProps,
    padding: '3px',
    color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)',
    backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(10px)',
    border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)'}`,
    boxShadow: isDarkMode ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.05)',
    borderRadius: '28px',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'
    },
    '&:active': {
      transform: 'scale(0.98)',
    },
    '&:disabled': {
      color: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
      opacity: 0.5, // Homologado a 0.5 para estado deshabilitado
      cursor: 'not-allowed',
    }
  };
});

const StyledDefaultButton = styled(IconButton)(({ theme, size }) => {
  // Mapeo de tamaños - forzar altura a 36px
  const sizeMap = {
    small: { padding: '4px', height: '36px', minHeight: '36px' },
    medium: { padding: '6px', height: '36px', minHeight: '36px' },
    large: { padding: '8px', height: '36px', minHeight: '36px' }
  };
  
  const sizeProps = sizeMap[size] || sizeMap.medium;
  
  const isDarkMode = theme.palette.mode === 'dark';

  return {
    ...sizeProps,
    borderRadius: '28px', // Más cuadrado, siguiendo estilo Big Sur
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(10px)', // Efecto de vidrio esmerilado de Big Sur
    boxShadow: isDarkMode ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.1)',
    border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)'}`,
    color: theme.palette.text.primary,
    transition: 'all 0.2s ease',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    fontWeight: 500,
    '&:hover': {
      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.9)',
    },
    '&:active': {
      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
      transform: 'scale(0.98)',
    },
    '&:disabled': {
      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.5)',
      border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
      color: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
      opacity: 0.5, // Homologado a 0.5 para estado deshabilitado
      cursor: 'not-allowed',
    }
  };
});

// Nuevo componente para el botón de adjuntar archivos
const StyledFileAttachmentButton = styled(IconButton)(({ theme, size }) => {
  const isDarkMode = theme.palette.mode === 'dark';
 
  // Forzar altura a 36px independientemente del tamaño
  const buttonHeight = '36px';
 
  return {
    width: buttonHeight,
    height: buttonHeight,
    padding: '8px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    borderRadius: '50%', // Circular para icono solo
    backgroundColor: isDarkMode
      ? 'rgba(255, 255, 255, 0.08)'
      : 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(10px)',
    border: `1px solid ${
      isDarkMode
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(0, 0, 0, 0.1)'
    }`,
    boxShadow: isDarkMode ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.05)',
    color: '#0071E3',
    transition: 'all 0.2s ease',
 
    // Estilos para hover
    '&:hover': {
      backgroundColor: isDarkMode
        ? 'rgba(255, 255, 255, 0.12)'
        : 'rgba(255, 255, 255, 0.95)'
    },
 
    // Estilos para active
    '&:active': {
      transform: 'scale(0.98)',
    },
 
    // Estilos para disabled - EXACTAMENTE iguales a FeatureButtonComponent
    '&:disabled': {
      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.5)',
      border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
      color: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
      opacity: 0.7, // Homologado a 0.5 para estado deshabilitado
      cursor: 'not-allowed',
    }
  };
});

export default AGTButton;